import React from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import useAppziWidget from "utils/hooks/useAppziWidget";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import { urlPaths } from "enums/urlPaths";

import drax_lighthouse_animation from "assets/animations/drax_lighthouse_animation.gif";
import drax_lighthouse_animation_blue from "assets/animations/drax_lighthouse_animation-blue.gif";
import oraganisations_search from "assets/images/pngs/organisations_search.png";
import styles from "./ResultsNotFound.styles";

const ImageVersions = {
    white: drax_lighthouse_animation,
    blue: drax_lighthouse_animation_blue,
};

const ResultsNotFound = ({
    searchTerm = "",
    showContact = true,
    showImage = true,
    imageSize = 440,
    imageVersion = "white",
    showImageOnly = true,
    message = "",
}: {
    searchTerm?: string;
    showContact?: boolean;
    showImage?: boolean;
    imageSize?: number;
    imageVersion?: keyof typeof ImageVersions;
    showImageOnly?: boolean;
    message?: string | MessageDescriptor[];
}) => {
    const classes = styles.resultsNotFound();
    const { handleOpenWidget } = useAppziWidget();
    const navigate = useNavigate();

    const openChatWidget = (event: React.SyntheticEvent) => {
        event.nativeEvent.preventDefault();
        handleOpenWidget();
    };

    const sendUsAMessage = (
        <button type="button" className={classes.linkButton} onClick={openChatWidget}>
            send us a message
        </button>
    );

    const onMouseDown = (ev: React.MouseEvent) => ev.preventDefault();

    const handleBackToHubManagement = () => {
        navigate(urlPaths.HubManagement);
    };

    return (
        <div className={classes.resultsNotFound}>
            <Grid container direction="column" justifyContent="center" alignItems="center">
                {showImage ? (
                    <Grid item className={classes.imgBox}>
                        {showImageOnly ? (
                            <img
                                style={{ width: imageSize }}
                                src={ImageVersions[imageVersion]}
                                alt="Results Not Found"
                            />
                        ) : (
                            <>
                                <Typography className={classes.startSearchText_Welcome} paragraph={true}>
                                    <FormattedMessage id="searchCompany.startSearch.text1" />
                                </Typography>

                                <Grid item>
                                    <img style={{ width: "300px" }} src={oraganisations_search} alt="Start Search" />
                                </Grid>

                                <Typography className={classes.startSearchText} paragraph={true}>
                                    <FormattedMessage id="searchCompany.startSearch.text2" />
                                </Typography>
                            </>
                        )}
                    </Grid>
                ) : null}

                <Grid
                    container
                    className={classes.resultsNotFound}
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Grid item className={classes.textBox}>
                        {searchTerm ? (
                            <Typography style={{ marginBottom: "8px" }} paragraph>
                                <FormattedMessage
                                    id="searchCompany.noResultsFound.noResults"
                                    values={{
                                        searchTerm: <b>{searchTerm}</b>,
                                    }}
                                />
                            </Typography>
                        ) : Array.isArray(message) ? (
                            <Box display="flex" flexDirection="column" justifyItems="center" marginBottom={2}>
                                <Typography paragraph align="center" marginBottom={2}>
                                    <FormattedMessage id={"hubManagement.notFound.messageTitle"} />
                                </Typography>
                                {message.map((msg, i) => (
                                    <Typography key={i} variant="subtitle2" align="center" marginBottom={0.5}>
                                        {msg.id === "hubManagement.notFound.message2" ? (
                                            <FormattedMessage
                                                id={msg.id}
                                                values={{
                                                    hubManagement: (
                                                        <button
                                                            type="button"
                                                            className={classes.linkButton}
                                                            onMouseDown={handleBackToHubManagement}
                                                        >
                                                            Hub Management
                                                        </button>
                                                    ),
                                                }}
                                            />
                                        ) : msg.id === "hubManagement.notFound.message4" ? (
                                            <FormattedMessage
                                                id={msg.id}
                                                values={{
                                                    turk: (
                                                        <a
                                                            className={classes.linkButton}
                                                            target="_blank"
                                                            href="https://data-updates.drxdata.com/company"
                                                            onMouseDown={onMouseDown}
                                                            rel="noopener noreferrer"
                                                        >
                                                            Turk
                                                        </a>
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <FormattedMessage id={msg.id} />
                                        )}
                                    </Typography>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="h4">{message}</Typography>
                        )}

                        {showContact ? (
                            <Typography className={classes.text2} paragraph>
                                <FormattedMessage
                                    id="searchCompany.noResultsFound.subText"
                                    values={{
                                        sendUsAMessage,
                                        br: <br />,
                                    }}
                                />
                            </Typography>
                        ) : undefined}
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};

export default ResultsNotFound;
